import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { GraduationCap, Mail, Lock, User, ArrowLeft, Loader2, Target, Clock } from 'lucide-react';

const Register = () => {
  const navigate = useNavigate();
  const { register, continueAsGuest } = useAuth();
  const [step, setStep] = useState(1); // 1: track selection, 2: account details
  const [selectedTrack, setSelectedTrack] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const tracks = [
    { id: 'PMP', name: 'PMP', fullName: 'Project Management Professional', questions: 180, time: 230, color: 'border-blue-500 bg-blue-50 dark:bg-blue-950/20' },
    { id: 'CAPM', name: 'CAPM', fullName: 'Certified Associate in Project Management', questions: 150, time: 180, color: 'border-purple-500 bg-purple-50 dark:bg-purple-950/20' },
    { id: 'PSM', name: 'Scrum Master', fullName: 'Professional Scrum Master', questions: 80, time: 60, color: 'border-green-500 bg-green-50 dark:bg-green-950/20' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptedTerms) {
      toast.error('Please accept the Terms of Use and Privacy Policy');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    
    try {
      await register(email, password, name, selectedTrack);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    if (!selectedTrack) {
      toast.error('Please select a certification track first');
      return;
    }
    if (!acceptedTerms) {
      toast.error('Please accept the Terms of Use and Privacy Policy');
      return;
    }
    // Store track selection for OAuth callback
    localStorage.setItem('pendingTrack', selectedTrack);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGuestAccess = async () => {
    if (!selectedTrack) {
      toast.error('Please select a certification track first');
      return;
    }
    setGuestLoading(true);
    try {
      await continueAsGuest(selectedTrack);
      toast.success('Welcome! You have 50 free questions.');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to create guest session');
    } finally {
      setGuestLoading(false);
    }
  };

  // Step 1: Track Selection
  if (step === 1) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4">
        <div className="max-w-2xl mx-auto pt-8">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-[#0047AB] transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-2xl bg-[#0047AB] flex items-center justify-center">
                <GraduationCap className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-3xl font-bold tracking-tight">Choose Your Certification</h1>
            <p className="text-slate-600 dark:text-slate-400 mt-2">
              Select the certification you want to prepare for
            </p>
            <p className="text-sm text-[#0047AB] mt-1">
              Note: You cannot change this after registration
            </p>
          </div>

          <div className="space-y-3 mb-8">
            {tracks.map((track) => (
              <Card
                key={track.id}
                className={`cursor-pointer transition-all ${
                  selectedTrack === track.id 
                    ? `${track.color} border-2` 
                    : 'border-slate-200 dark:border-slate-800 hover:border-slate-300'
                }`}
                onClick={() => setSelectedTrack(track.id)}
                data-testid={`track-${track.id.toLowerCase()}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedTrack === track.id ? 'border-[#0047AB]' : 'border-slate-300'
                      }`}>
                        {selectedTrack === track.id && (
                          <div className="w-3 h-3 rounded-full bg-[#0047AB]" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold">{track.fullName}</h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400">{track.name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
                      <span className="flex items-center gap-1">
                        <Target className="w-4 h-4" />
                        {track.questions}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {track.time}m
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Button
            className="w-full h-12 bg-[#0047AB] hover:bg-[#003380] text-white rounded-xl"
            onClick={() => setStep(2)}
            disabled={!selectedTrack}
            data-testid="continue-btn"
          >
            Continue
          </Button>
        </div>
      </div>
    );
  }

  // Step 2: Account Details
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <button 
            onClick={() => setStep(1)}
            className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-[#0047AB] transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Change Track
          </button>
          
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 rounded-2xl bg-[#0047AB] flex items-center justify-center">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Create Account</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">
            Preparing for <span className="font-semibold text-[#0047AB]">{tracks.find(t => t.id === selectedTrack)?.fullName}</span>
          </p>
        </div>

        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 h-12"
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12"
                    required
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-12"
                    required
                    data-testid="register-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 h-12"
                    required
                    data-testid="register-confirm-password-input"
                  />
                </div>
              </div>

              {/* Terms and Conditions Checkbox */}
              <div className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                <Checkbox 
                  id="terms" 
                  checked={acceptedTerms}
                  onCheckedChange={setAcceptedTerms}
                  className="mt-1"
                  data-testid="terms-checkbox"
                />
                <Label htmlFor="terms" className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed cursor-pointer">
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" className="text-[#0047AB] hover:underline">Terms of Use</Link>,{' '}
                  <Link to="/privacy-policy" target="_blank" className="text-[#0047AB] hover:underline">Privacy Policy</Link>, and{' '}
                  <Link to="/disclaimer" target="_blank" className="text-[#0047AB] hover:underline">Disclaimer</Link>.
                  I understand that this is an independent exam preparation platform.
                </Label>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-[#0047AB] hover:bg-[#003380] text-white rounded-xl"
                disabled={loading || !acceptedTerms}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>

            <div className="relative my-6">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-slate-900 px-2 text-xs text-slate-500">
                OR
              </span>
            </div>

            <div className="space-y-3">
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 rounded-xl"
                onClick={handleGoogleSignup}
                data-testid="google-signup-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full h-12 rounded-xl text-slate-600 dark:text-slate-400"
                onClick={handleGuestAccess}
                disabled={guestLoading}
                data-testid="guest-access-btn"
              >
                {guestLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating session...
                  </>
                ) : (
                  'Continue as Guest (50 free questions)'
                )}
              </Button>
            </div>

            <p className="text-center text-sm text-slate-600 dark:text-slate-400 mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-[#0047AB] hover:underline font-medium" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
